const AUTO_SCROLL_SPEED_PX_PER_SEC = 16;

const getHorizontalGapWidth = (element) => {
  const style = window.getComputedStyle(element);
  const rawGap = style.columnGap && style.columnGap !== 'normal' ? style.columnGap : style.gap;
  const gap = Number.parseFloat(rawGap);
  return Number.isFinite(gap) ? gap : 0;
};

function enableCampaignAutoScrollByScrollLeft(scrollContainer, options = {}) {
  const { loop = false } = options;
  const shouldLoop = Boolean(loop);

  const prepareLoop = () => {
    if (!shouldLoop) return null;
    if (!scrollContainer || scrollContainer.dataset.storeeelAutoScrollLoopPrepared === 'true') {
      const w = Number(scrollContainer?.dataset?.storeeelAutoScrollLoopWidth);
      return Number.isFinite(w) ? w : null;
    }

    // 現在の子要素を前後に複製し、左右どちらにも無限スクロールできるようにする
    const originalWidth = scrollContainer.scrollWidth;
    if (!(originalWidth > 0) || originalWidth <= scrollContainer.clientWidth) return null;

    const children = Array.from(scrollContainer.children);
    if (children.length === 0) return null;

    const fragBefore = document.createDocumentFragment();
    const fragAfter = document.createDocumentFragment();
    children.forEach((child) => {
      const cloneBefore = child.cloneNode(true);
      cloneBefore.dataset.storeeelAutoScrollClone = 'true';
      fragBefore.appendChild(cloneBefore);

      const cloneAfter = child.cloneNode(true);
      cloneAfter.dataset.storeeelAutoScrollClone = 'true';
      fragAfter.appendChild(cloneAfter);
    });
    scrollContainer.insertBefore(fragBefore, scrollContainer.firstChild);
    scrollContainer.appendChild(fragAfter);

    const loopWidth = originalWidth + getHorizontalGapWidth(scrollContainer);
    scrollContainer.dataset.storeeelAutoScrollLoopPrepared = 'true';
    scrollContainer.dataset.storeeelAutoScrollLoopWidth = String(loopWidth);
    return loopWidth;
  };

  const normalizeLoopScrollLeft = (loopWidth) => {
    if (!loopWidth) return;
    let next = scrollContainer.scrollLeft;
    // 中央（オリジナル領域）に戻すことで左右スワイプのループを安定させる
    if (next >= loopWidth * 2) {
      next -= loopWidth;
    } else if (next < loopWidth) {
      next += loopWidth;
    }
    if (next !== scrollContainer.scrollLeft) {
      scrollContainer.scrollLeft = next;
    }
  };

  // モバイルの慣性スクロールと自動スクロールが scrollLeft を奪い合うのを防ぐため、
  // touchend 後は「指が離れてから一定時間経過」かつ「スクロールが落ち着いた」後に再開する。
  let pos = 0;
  const speedPerSec = AUTO_SCROLL_SPEED_PX_PER_SEC; // px/秒
  const maxFrameDtMs = 50; // フレーム落ち時の急なジャンプを防ぐ
  let rafId = null;
  let isPaused = false;
  let isInView = false;
  let hasEverIntersected = false;
  let resumeRequested = false;
  let resumeNotBeforeTs = null;
  let settleTimerId = null;
  let lastTs = null;
  const settleDelayMs = 120;
  // 慣性スクロール完了後に余裕を持たせるため少し長めに待つ
  const resumeDelayAfterTouchEndMs = 1600;

  let loopWidth = prepareLoop();

  const syncPositionDataset = () => {
    scrollContainer.dataset.storeeelAutoScrollPosition = String(Math.max(0, pos));
  };
  scrollContainer.storeeelSetAutoScrollPosition = (next) => {
    if (!Number.isFinite(next)) return;
    pos = Math.max(0, next);
    scrollContainer.scrollLeft = pos;
    syncPositionDataset();
  };
  scrollContainer.storeeelRefreshAutoScrollLoop = () => {
    if (!shouldLoop) return;
    Array.from(scrollContainer.children)
      .filter((child) => child.dataset?.storeeelAutoScrollClone === 'true')
      .forEach((child) => child.remove());
    delete scrollContainer.dataset.storeeelAutoScrollLoopPrepared;
    delete scrollContainer.dataset.storeeelAutoScrollLoopWidth;
    loopWidth = prepareLoop();
    pos = scrollContainer.scrollLeft;
    normalizeLoopScrollLeft(loopWidth);
    syncPositionDataset();
  };

  const smoothScroll = (ts) => {
    if (isPaused || !isInView) return;

    // 外部（手動スクロール等）で scrollLeft が変わったときだけ追従する
    // （毎フレーム同期すると、小数の蓄積が消えてスクロールしない環境がある）
    const currentScrollLeft = scrollContainer.scrollLeft;
    if (Math.abs(currentScrollLeft - pos) > 1) {
      pos = currentScrollLeft;
    }
    if (!Number.isFinite(lastTs)) {
      lastTs = ts;
    }
    const dt = Math.min(maxFrameDtMs, Math.max(0, ts - lastTs));
    lastTs = ts;
    pos += (speedPerSec * dt) / 1000;
    scrollContainer.scrollLeft = pos;

    if (loopWidth) {
      // 中央領域でループを維持
      if (pos >= loopWidth * 2) {
        pos -= loopWidth;
        scrollContainer.scrollLeft = pos;
      } else if (pos < loopWidth) {
        pos += loopWidth;
        scrollContainer.scrollLeft = pos;
      }
    } else {
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      if (pos >= maxScrollLeft) {
        cancelAnimationFrame(rafId);
        return;
      }
    }

    syncPositionDataset();
    rafId = requestAnimationFrame(smoothScroll);
  };

  const scheduleResumeWhenScrollSettles = () => {
    if (!isInView) return;
    if (settleTimerId) clearTimeout(settleTimerId);
    const now = Date.now();
    const notBeforeDelay = resumeNotBeforeTs ? Math.max(0, resumeNotBeforeTs - now) : 0;
    const delay = Math.max(settleDelayMs, notBeforeDelay);
    settleTimerId = setTimeout(() => {
      settleTimerId = null;
      if (!resumeRequested || !isInView) return;
      if (resumeNotBeforeTs && Date.now() < resumeNotBeforeTs) {
        scheduleResumeWhenScrollSettles();
        return;
      }
      resumeRequested = false;
      resumeNotBeforeTs = null;
      isPaused = false;
      lastTs = null;
      pos = scrollContainer.scrollLeft;
      normalizeLoopScrollLeft(loopWidth);
      syncPositionDataset();
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(smoothScroll);
    }, delay);
  };

  const pauseScroll = () => {
    resumeRequested = false;
    resumeNotBeforeTs = null;
    if (settleTimerId) clearTimeout(settleTimerId);
    settleTimerId = null;
    isPaused = true;
    lastTs = null;
    cancelAnimationFrame(rafId);
  };
  scrollContainer.storeeelPauseAutoScroll = pauseScroll;

  const requestResume = () => {
    resumeRequested = true;
    resumeNotBeforeTs = Date.now() + resumeDelayAfterTouchEndMs;
    isPaused = true;
    scheduleResumeWhenScrollSettles();
  };

  const resumeScroll = () => {
    if (!isInView) return;
    isPaused = false;
    lastTs = null;
    pos = scrollContainer.scrollLeft;
    normalizeLoopScrollLeft(loopWidth);
    syncPositionDataset();
    rafId = requestAnimationFrame(smoothScroll);
  };

  // 画面内外の切り替えを監視（見えている間だけ動かす）
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const firstIntersect = !hasEverIntersected;
          hasEverIntersected = true;
          isInView = true;
          if (firstIntersect && loopWidth) {
            scrollContainer.scrollLeft = loopWidth;
            pos = loopWidth;
          } else {
            pos = scrollContainer.scrollLeft;
          }
          normalizeLoopScrollLeft(loopWidth);
          syncPositionDataset();
          resumeRequested = false;
          resumeNotBeforeTs = null;
          if (settleTimerId) clearTimeout(settleTimerId);
          settleTimerId = null;
          lastTs = null;
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(smoothScroll);
        } else {
          isInView = false;
          resumeRequested = false;
          resumeNotBeforeTs = null;
          if (settleTimerId) clearTimeout(settleTimerId);
          settleTimerId = null;
          lastTs = null;
          cancelAnimationFrame(rafId);
        }
      });
    },
    { threshold: 0.5 }
  );
  observer.observe(scrollContainer);

  // 初期表示時に IntersectionObserver の発火待ちで止まらないよう、いったん動かしておく
  // （画面外なら observer の else 側で停止する）
  isInView = true;
  hasEverIntersected = true;
  if (loopWidth) {
    scrollContainer.scrollLeft = loopWidth;
    pos = loopWidth;
  } else {
    pos = scrollContainer.scrollLeft;
  }
  normalizeLoopScrollLeft(loopWidth);
  syncPositionDataset();
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(smoothScroll);

  scrollContainer.addEventListener('mouseenter', pauseScroll);
  scrollContainer.addEventListener('mouseleave', resumeScroll);
  scrollContainer.addEventListener('touchstart', pauseScroll, { passive: true });
  scrollContainer.addEventListener('touchend', requestResume, { passive: true });
  scrollContainer.addEventListener('touchcancel', requestResume, { passive: true });
  scrollContainer.addEventListener('scroll', () => {
    normalizeLoopScrollLeft(loopWidth);
    pos = scrollContainer.scrollLeft;
    syncPositionDataset();
    if (!resumeRequested) return;
    scheduleResumeWhenScrollSettles();
  }, { passive: true });

  return () => {
    observer.disconnect();
    if (settleTimerId) clearTimeout(settleTimerId);
    settleTimerId = null;
    cancelAnimationFrame(rafId);
    scrollContainer.removeEventListener('mouseenter', pauseScroll);
    scrollContainer.removeEventListener('mouseleave', resumeScroll);
    scrollContainer.removeEventListener('touchstart', pauseScroll);
    scrollContainer.removeEventListener('touchend', requestResume);
    scrollContainer.removeEventListener('touchcancel', requestResume);
    delete scrollContainer.storeeelSetAutoScrollPosition;
    delete scrollContainer.storeeelRefreshAutoScrollLoop;
    delete scrollContainer.storeeelPauseAutoScroll;
  };
}

function enableCampaignAutoScrollByTransform(scrollContainer, options = {}) {
  const { loop = false } = options;
  const shouldLoop = Boolean(loop);
  const originalOverflowX = scrollContainer.style.overflowX;

  // 既存DOMを内側トラックへ移し、GPU合成されやすい transform で動かす
  const track = document.createElement('div');
  track.className = 'storeeel-auto-scroll-track';
  while (scrollContainer.firstChild) {
    track.appendChild(scrollContainer.firstChild);
  }
  scrollContainer.appendChild(track);

  track.style.display = 'flex';
  track.style.alignItems = 'inherit';
  track.style.width = 'max-content';
  track.style.willChange = 'transform';
  const computedStyle = window.getComputedStyle(scrollContainer);
  const gap = computedStyle.columnGap && computedStyle.columnGap !== 'normal'
    ? computedStyle.columnGap
    : computedStyle.gap;
  if (gap && gap !== 'normal') {
    track.style.gap = gap;
  }
  // transform移動を優先するためネイティブスクロールは無効化
  scrollContainer.style.overflowX = 'hidden';

  const prepareLoop = () => {
    if (!shouldLoop) return null;

    const originalWidth = track.scrollWidth;
    if (!(originalWidth > 0) || originalWidth <= scrollContainer.clientWidth) return null;

    const children = Array.from(track.children);
    if (children.length === 0) return null;

    const fragBefore = document.createDocumentFragment();
    const fragAfter = document.createDocumentFragment();
    children.forEach((child) => {
      const cloneBefore = child.cloneNode(true);
      cloneBefore.dataset.storeeelAutoScrollClone = 'true';
      fragBefore.appendChild(cloneBefore);

      const cloneAfter = child.cloneNode(true);
      cloneAfter.dataset.storeeelAutoScrollClone = 'true';
      fragAfter.appendChild(cloneAfter);
    });

    track.insertBefore(fragBefore, track.firstChild);
    track.appendChild(fragAfter);

    const loopWidth = originalWidth + getHorizontalGapWidth(track);
    scrollContainer.dataset.storeeelAutoScrollLoopPrepared = 'true';
    scrollContainer.dataset.storeeelAutoScrollLoopWidth = String(loopWidth);
    return loopWidth;
  };

  let pos = 0;
  const speedPerSec = AUTO_SCROLL_SPEED_PX_PER_SEC;
  const maxFrameDtMs = 50;
  let rafId = null;
  let isPaused = false;
  let isInView = false;
  let hasEverIntersected = false;
  let lastTs = null;
  let isHovering = false;
  let isDragging = false;
  let dragStartX = 0;
  let dragStartPos = 0;
  let isTouchDragging = false;
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartPos = 0;
  let isTouchHorizontalMove = false;

  let loopWidth = prepareLoop();
  scrollContainer.dataset.storeeelAutoScrollRenderMode = 'transform';

  const applyPosition = () => {
    track.style.transform = `translate3d(${-pos}px, 0, 0)`;
    scrollContainer.dataset.storeeelAutoScrollPosition = String(Math.max(0, pos));
  };
  scrollContainer.storeeelSetAutoScrollPosition = (next) => {
    if (!Number.isFinite(next)) return;
    pos = Math.max(0, next);
    normalizeLoopPos();
    applyPosition();
  };
  scrollContainer.storeeelRefreshAutoScrollLoop = () => {
    if (!shouldLoop) return;
    Array.from(track.children)
      .filter((child) => child.dataset?.storeeelAutoScrollClone === 'true')
      .forEach((child) => child.remove());
    delete scrollContainer.dataset.storeeelAutoScrollLoopPrepared;
    delete scrollContainer.dataset.storeeelAutoScrollLoopWidth;
    loopWidth = prepareLoop();
    normalizeLoopPos();
    clampNonLoopPos();
    applyPosition();
  };

  const normalizeLoopPos = () => {
    if (!loopWidth) return;
    // ドラッグ等で大きく移動しても中央帯(w..2w)に収める
    while (pos >= loopWidth * 2) {
      pos -= loopWidth;
    }
    while (pos < loopWidth) {
      pos += loopWidth;
    }
  };

  const clampNonLoopPos = () => {
    if (loopWidth) return;
    const maxPos = Math.max(0, track.scrollWidth - scrollContainer.clientWidth);
    if (pos < 0) pos = 0;
    if (pos > maxPos) pos = maxPos;
  };

  const smoothScroll = (ts) => {
    if (isPaused || !isInView) return;

    if (!Number.isFinite(lastTs)) {
      lastTs = ts;
    }
    const dt = Math.min(maxFrameDtMs, Math.max(0, ts - lastTs));
    lastTs = ts;
    pos += (speedPerSec * dt) / 1000;

    if (loopWidth) {
      normalizeLoopPos();
    } else {
      const maxPos = Math.max(0, track.scrollWidth - scrollContainer.clientWidth);
      if (pos >= maxPos) {
        pos = maxPos;
        applyPosition();
        cancelAnimationFrame(rafId);
        return;
      }
    }

    applyPosition();
    rafId = requestAnimationFrame(smoothScroll);
  };

  const pauseScroll = () => {
    isPaused = true;
    lastTs = null;
    cancelAnimationFrame(rafId);
  };
  scrollContainer.storeeelPauseAutoScroll = pauseScroll;

  const resumeScroll = () => {
    if (!isInView) return;
    if (isHovering || isDragging) return;
    isPaused = false;
    lastTs = null;
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(smoothScroll);
  };

  const handleMouseEnter = () => {
    isHovering = true;
    pauseScroll();
  };

  const handleMouseLeave = () => {
    isHovering = false;
    resumeScroll();
  };

  const handleMouseDown = (e) => {
    isDragging = true;
    dragStartX = e.clientX;
    dragStartPos = pos;
    pauseScroll();
    scrollContainer.classList.add('dragging');
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const dx = e.clientX - dragStartX;
    pos = dragStartPos - (dx * 1.2);
    normalizeLoopPos();
    clampNonLoopPos();
    applyPosition();
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    isDragging = false;
    scrollContainer.classList.remove('dragging');
    resumeScroll();
  };

  const handleTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    isTouchDragging = true;
    isTouchHorizontalMove = false;
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
    touchStartPos = pos;
    pauseScroll();
  };

  const handleTouchMove = (e) => {
    if (!isTouchDragging) return;
    const touch = e.touches?.[0];
    if (!touch) return;

    const dx = touch.clientX - touchStartX;
    const dy = touch.clientY - touchStartY;

    // 縦スクロールを阻害しないよう、横方向優勢と判定できる時だけ横移動を反映する
    if (!isTouchHorizontalMove) {
      isTouchHorizontalMove = Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy);
    }
    if (!isTouchHorizontalMove) return;

    e.preventDefault();
    pos = touchStartPos - dx;
    normalizeLoopPos();
    clampNonLoopPos();
    applyPosition();
  };

  const handleTouchEnd = () => {
    if (!isTouchDragging) return;
    isTouchDragging = false;
    isTouchHorizontalMove = false;
    resumeScroll();
  };

  const handleWheel = (e) => {
    const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
    if (!Number.isFinite(delta) || delta === 0) return;
    e.preventDefault();
    pauseScroll();
    pos += delta;
    normalizeLoopPos();
    clampNonLoopPos();
    applyPosition();
    resumeScroll();
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const firstIntersect = !hasEverIntersected;
          hasEverIntersected = true;
          isInView = true;
          if (firstIntersect && loopWidth) {
            pos = loopWidth;
          }
          normalizeLoopPos();
          applyPosition();
          lastTs = null;
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(smoothScroll);
        } else {
          isInView = false;
          lastTs = null;
          cancelAnimationFrame(rafId);
        }
      });
    },
    { threshold: 0.5 }
  );
  observer.observe(scrollContainer);

  // 初期表示時に observer 発火待ちで止まらないよう、先に動かしておく
  isInView = true;
  hasEverIntersected = true;
  if (loopWidth) {
    pos = loopWidth;
  }
  normalizeLoopPos();
  applyPosition();
  cancelAnimationFrame(rafId);
  rafId = requestAnimationFrame(smoothScroll);

  scrollContainer.addEventListener('mouseenter', handleMouseEnter);
  scrollContainer.addEventListener('mouseleave', handleMouseLeave);
  scrollContainer.addEventListener('mousedown', handleMouseDown);
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
  scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
  scrollContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
  scrollContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
  scrollContainer.addEventListener('touchcancel', handleTouchEnd, { passive: true });

  return () => {
    observer.disconnect();
    cancelAnimationFrame(rafId);
    scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
    scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    scrollContainer.removeEventListener('mousedown', handleMouseDown);
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    scrollContainer.removeEventListener('wheel', handleWheel);
    scrollContainer.removeEventListener('touchstart', handleTouchStart);
    scrollContainer.removeEventListener('touchmove', handleTouchMove);
    scrollContainer.removeEventListener('touchend', handleTouchEnd);
    scrollContainer.removeEventListener('touchcancel', handleTouchEnd);
    Array.from(track.children)
      .filter((child) => child.dataset?.storeeelAutoScrollClone === 'true')
      .forEach((child) => child.remove());
    track.style.transform = '';
    while (track.firstChild) {
      scrollContainer.insertBefore(track.firstChild, track);
    }
    track.remove();
    scrollContainer.style.overflowX = originalOverflowX;
    delete scrollContainer.dataset.storeeelAutoScrollLoopPrepared;
    delete scrollContainer.dataset.storeeelAutoScrollLoopWidth;
    delete scrollContainer.dataset.storeeelAutoScrollRenderMode;
    delete scrollContainer.dataset.storeeelAutoScrollPosition;
    delete scrollContainer.storeeelSetAutoScrollPosition;
    delete scrollContainer.storeeelRefreshAutoScrollLoop;
    delete scrollContainer.storeeelPauseAutoScroll;
  };
}

export function enableCampaignAutoScroll(scrollContainer, options = {}) {
  const { renderMode = 'scroll' } = options;
  if (renderMode === 'transform') {
    return enableCampaignAutoScrollByTransform(scrollContainer, options);
  }
  return enableCampaignAutoScrollByScrollLeft(scrollContainer, options);
}
